import { Button, MenuItem, Spinner } from "@blueprintjs/core"
import { ItemPredicate, ItemRenderer, Select } from "@blueprintjs/select"
import { FieldValues, Path, useController, useFormContext } from "react-hook-form"

import { FlexContainerAndItem } from "../../../../styled/flexbox.styled"
import { ToastError } from "../../../components"
import useTradingSectors from "../hooks/useTradingSectors"
import { SectorLevel, Sector } from "../types"

interface ISectorSelectProps<T extends FieldValues> {
  level: SectorLevel
  name: Path<T>
}

const SectorSelect = <T extends FieldValues>({ level, name }: ISectorSelectProps<T>) => {
  const { control } = useFormContext<T>()

  const {
    field: { value, onChange },
    fieldState: { isDirty },
  } = useController({
    control,
    name,
  })

  /**
   * Get all sectors
   */
  const { sectors, error, isLoading } = useTradingSectors()

  /**
   * generate Menu to render in the dropdown
   */
  const renderSectors: ItemRenderer<Sector> = (sector, { handleClick, modifiers }) => {
    if (!modifiers.matchesPredicate) return null
    return (
      <MenuItem
        active={modifiers.active}
        key={sector.id}
        text={sector.name}
        onClick={handleClick}
        roleStructure="listoption"
      />
    )
  }

  /**
   * Filter the sector based on the query
   */
  const filterSector: ItemPredicate<Sector> = (query, item, index, exactMatch) => {
    const normalizedQuery = query.toLowerCase()
    if (!item.name) {
      return false
    }
    const normalizedTitle = item.name?.toLowerCase()

    if (exactMatch) {
      return normalizedTitle === normalizedQuery
    } else {
      return normalizedTitle.indexOf(normalizedQuery) >= 0
    }
  }

  return (
    <FlexContainerAndItem justifyContent="center" flexGrow={1}>
      {isLoading ? (
        <Spinner size={15} />
      ) : (
        <Select
          items={sectors?.length ? sectors.filter(sector => sector.level === level) : []}
          itemRenderer={renderSectors}
          itemPredicate={filterSector}
          onItemSelect={sector => onChange(sector)}
          noResults={<MenuItem disabled={true} text="No Sectors found" />}
          resetOnSelect={true}
          resetOnClose={true}
          disabled={error ? true : false}
          fill
        >
          <Button
            fill
            small
            intent={isDirty ? "warning" : "none"}
            text={(value as Sector)?.name ? (value as Sector).name : "Select a sector"}
            rightIcon="caret-down"
          />
        </Select>
      )}
      <ToastError
        error={error}
        errorMessage="Unable to get sectors, please try to refresh the page."
      />
    </FlexContainerAndItem>
  )
}

export default SectorSelect
